import memoizeOne from 'memoize-one'
import { uniq, round } from 'lodash'
import {
  getPDLDefaultGrade,
  getPDLDefaultSubject,
  getPDLDefaultSubjectId,
  getPDLGradeId,
} from '../../../AssessmentCreate/components/CreateAITest/ducks/helpers'
import { getPerfomancePercentage } from '../TableDisplay'

export const getStandardListWithStudentCount = ({
  standards,
  studentWiseStandardPerformance,
  standardPerformanceByStudentIdAndStandardId,
  masteryRange,
}) => {
  const studentIds = Object.keys(studentWiseStandardPerformance)

  return standards
    .map((standard) => {
      const { _id: standardId } = standard
      let studentCount = 0

      studentIds.forEach((studentId) => {
        const performancePercentage =
          standardPerformanceByStudentIdAndStandardId[
            `${studentId}_${standardId}`
          ]
        if (
          performancePercentage >= masteryRange[0] &&
          performancePercentage <= masteryRange[1]
        ) {
          studentCount += 1
        }
      })

      return { ...standard, studentCount }
    })
    .sort((a, b) => b.studentCount - a.studentCount)
}

export const getStandardPerformanceByStudentIdAndStandardId = ({
  studentWiseStandardPerformance,
}) => {
  const standardPerformanceByStudentIdAndStandardId = {}

  Object.keys(studentWiseStandardPerformance).forEach((studentId) => {
    const studentData = studentWiseStandardPerformance[studentId]
    const { standards = [] } = studentData
    standards.forEach((standard) => {
      const { _id: standardId, perfomancePercentage } = standard
      standardPerformanceByStudentIdAndStandardId[
        `${studentId}_${standardId}`
      ] = perfomancePercentage
    })
  })

  return standardPerformanceByStudentIdAndStandardId
}

const getStandardsPerformanceData = (
  standards,
  testActivities,
  useOnlySubmitted
) => {
  return standards.map((standard) => {
    return {
      ...standard,
      performanceSummary: getPerfomancePercentage(
        testActivities,
        standard,
        useOnlySubmitted
      ),
    }
  })
}

export const getStandardsPerformanceDataMemoized = memoizeOne(
  getStandardsPerformanceData
)

const calculateMedian = (values) => {
  if (values.length === 0) {
    return 0
  }

  const sortedValues = values.sort((a, b) => a - b)
  const middle = Math.floor(sortedValues.length / 2)

  if (sortedValues.length % 2 !== 0) {
    return sortedValues[middle]
  }

  return round((sortedValues[middle - 1] + sortedValues[middle]) / 2, 2)
}

export const getILPTransformPropData = ({ testData, selectedStudents }) => {
  const { title = '', grades = [], subjects = [] } = testData || {}

  const pdlDefaultGrade = getPDLDefaultGrade(grades)
  const pdlDefaultGradeId = getPDLGradeId(pdlDefaultGrade)
  const pdlDefaultSubject = getPDLDefaultSubject(subjects)
  const pdlDefaultSubjectId = getPDLDefaultSubjectId(pdlDefaultSubject)

  const standardIds = selectedStudents
    .map(({ standards }) =>
      standards.map(({ pdlId = '' }) => `${pdlId}`).filter(Boolean)
    )
    .flat()
    .filter(Boolean)

  const standardsWisePerformancePercentage = {}

  selectedStudents.forEach(({ standards }) => {
    standards.forEach(({ identifier, perfomancePercentage }) => {
      if (standardsWisePerformancePercentage[identifier]) {
        standardsWisePerformancePercentage[identifier].push(
          perfomancePercentage
        )
      } else {
        standardsWisePerformancePercentage[identifier] = [perfomancePercentage]
      }
    })
  })

  Object.keys(standardsWisePerformancePercentage).forEach((identifier) => {
    standardsWisePerformancePercentage[identifier] = calculateMedian(
      standardsWisePerformancePercentage[identifier]
    )
  })

  const content = `You are given the median performance percentages of selected students across various learning standards in a class:

${JSON.stringify(standardsWisePerformancePercentage)}

Each element in the object represents the median score for a specific standard. Based on this data and the metadata provided, generate personalized practice content for the students.

Use Bloom’s Taxonomy to determine the appropriate cognitive level for each standard based on its median value:

- 0–30: Focus on "Remember" and "Understand" level questions.
- 31–60: Focus on "Apply" and "Analyze" level questions.
- 61–100: Focus on "Evaluate" and "Create" level questions.

Guidelines:
- Ensure all generated questions are **evenly distributed across the standards**.
- If the number of questions is less than the number of standards, **create multi-standard questions** (i.e., questions that address more than one standard).
- Tailor the difficulty and cognitive skill level of each question according to the standard's median value range as specified above.
`
  return {
    title,
    ...(pdlDefaultGradeId ? { gradeLevelId: pdlDefaultGradeId } : {}),
    ...(pdlDefaultSubjectId ? { subjectId: pdlDefaultSubjectId } : {}),
    standardIds: uniq(standardIds),
    content,
    type: 'TYPE_PEAR_ASSESSMENT_TEST',
  }
}

export const getStudentWiseStandardPerformance = ({
  studentWiseStandardPerformance,
  selectedStandardsIds,
  masteryRange,
}) => {
  return Object.keys(studentWiseStandardPerformance)
    .map((studentId) => {
      const filteredStandards = (
        studentWiseStandardPerformance?.[studentId]?.standards || []
      ).filter(
        (standard) =>
          selectedStandardsIds.includes(standard.id) &&
          standard.perfomancePercentage >= masteryRange[0] &&
          standard.perfomancePercentage <= masteryRange[1]
      )

      if (filteredStandards.length === 0) {
        return null
      }

      const totalMastery = filteredStandards.reduce((acc, standard) => {
        return acc + standard?.perfomancePercentage || 0
      }, 0)

      const avgMastery = filteredStandards.length
        ? round(totalMastery / filteredStandards.length)
        : 0

      return {
        ...studentWiseStandardPerformance[studentId],
        standards: filteredStandards,
        studentId,
        avgMastery,
      }
    })
    .filter(Boolean)
}

export const addSubjectToStandards = ({
  standards,
  allInterestedCurriculums,
}) => {
  return standards.map((standard) => {
    const interestedCurriculum = allInterestedCurriculums.find(
      (curriculum) => curriculum._id === standard.curriculumId
    )
    return { ...standard, subject: interestedCurriculum.subject }
  })
}

export const getSelectedStandardIds = ({ allStandardsList }) => {
  const selectedStandardsIds = allStandardsList
    .filter((_standard) => _standard.isChecked)
    .map((_standard) => _standard._id)

  return selectedStandardsIds
}
